import React, { useMemo, useState } from "react";
import { Alert, Button, Col, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";

const sortByLiftOperatorName = (rowA, rowB) => {
  const nameA = String(rowA.original?.name ?? "");
  const nameB = String(rowB.original?.name ?? "");

  return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
};

function LiftOperatorList({
  isAuthenticated,
  user,
  filter,
  data,
  updateTableState,
  saveRecord,
  validateRelatedRecord,
  liftOperatorOptions,
  propertyid,
  controlType,
  refreshDataSet,
  pageSize,
}) {
  const dataSet = "liftOperators";
  const dataSetPrimaryKey = "lift_operator_id";
  const tableData = Array.isArray(data) ? data : [];
  const options = useMemo(
    () =>
      (Array.isArray(liftOperatorOptions) ? liftOperatorOptions : [])
        .map((option) => {
          if (option && typeof option === "object" && "value" in option && "label" in option) {
            return option;
          }

          const value =
            option?.value ??
            option?.entity_id ??
            option?.entity_id_lift_operator ??
            option?.lift_operator_id ??
            option?.id ??
            null;

          const label =
            option?.label ??
            option?.name ??
            option?.lift_operator ??
            option?.entity_name ??
            option?.description ??
            (value != null ? String(value) : "");

          return {
            ...option,
            value,
            label,
          };
        })
        .filter((option) => option.value != null && String(option.value).trim() !== "" && String(option.label || "").trim() !== ""),
    [liftOperatorOptions]
  );

  const [errorMessage, setErrorMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const isSummaryMode = controlType === "liftOperatorSummaryTable" || controlType === "table2" || controlType === "liftOperatorTable";
  const isEntityDetailContext = controlType === "liftOperatorTable";
  const canAddRecords = !isSummaryMode && !isEntityDetailContext;
  const columnHelper = createColumnHelper();
  const createId = useMemo(() => createElementIdFactory("lift-operator-list"), []);
  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId, controlType || "propertyDetailLiftsTable"),
    [createId, controlType]
  );
  const selectMenuProps = useMemo(
    () => ({
      menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
      menuPosition: "fixed",
      styles: {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      },
    }),
    []
  );

  const resolvedPageSize =
    typeof pageSize === "number"
      ? pageSize
      : (isEntityDetailContext || isSummaryMode)
        ? PAGE_SIZES.entityDetailLiftOperatorsTab
        : PAGE_SIZES.propertyLiftsTab;

  const validateRecord = (record, rowIndex) => {
    if (typeof validateRelatedRecord !== "function") {
      return [true];
    }

    const records = tableData.map((operator, index) => index === rowIndex ? record : operator);
    const validation = validateRelatedRecord(record, dataSet, records);
    const result = Array.isArray(validation) ? validation : [true];
    setWarningMessage(result[0] && result[1] ? result[1] : "");
    return result;
  };

  const persistRecord = async (record) => {
    try {
      return await saveRecord(record, dataSet, dataSetPrimaryKey);
    } catch (err) {
      return { result: false, error_message: err?.message || "Save failed." };
    }
  };

  const updateRowPatch = (rowIndex, patch) => {
    if (rowIndex === null || rowIndex < 0 || !tableData[rowIndex]) {
      return;
    }

    const localData = [...tableData];
    localData[rowIndex] = {
      ...localData[rowIndex],
      ...patch,
      modified: true,
      last_edited_by: user,
    };

    updateTableState(localData, dataSet);
  };

  const saveRow = async (rowIndex, overrides = {}) => {
    const liveRow = tableData[rowIndex];
    if (!liveRow) {
      return false;
    }

    const record = {
      ...liveRow,
      ...overrides,
      last_edited_by: user,
    };

    const validation = validateRecord(record, rowIndex);
    if (!validation[0]) {
      setErrorMessage(validation[1] || "Validation failed.");
      setShowAlert(true);
      return false;
    }

    const result = await persistRecord(record);
    if (result && result.result === false) {
      setErrorMessage(result.error_message || "Failed to save record.");
      setShowAlert(true);
      return false;
    }

    const localData = [...tableData];
    localData[rowIndex] = {
      ...localData[rowIndex],
      ...record,
      ...(typeof result === "object" ? result : {}),
      modified: false,
      new_record: false,
      ...(result?.id ? { [dataSetPrimaryKey]: result.id } : {}),
    };
    updateTableState(localData, dataSet);

    if (typeof refreshDataSet === "function") {
      await refreshDataSet(dataSet);
    }

    return true;
  };

  const handleInlineSave = async ({ row, column, value }) => {
    const liveRow = tableData[row.index] || row.original || {};
    const record = {
      ...liveRow,
      ...(column?.id ? { [column.id]: value } : {}),
      last_edited_by: user,
    };

    const validation = validateRecord(record, row.index);
    if (!validation[0]) {
      setErrorMessage(validation[1] || "Validation failed.");
      setShowAlert(true);
      return false;
    }

    const result = await persistRecord(record);
    if (result && result.result === false) {
      setErrorMessage(result.error_message || "Failed to save record.");
      setShowAlert(true);
      return false;
    }

    const localData = [...tableData];
    localData[row.index] = {
      ...localData[row.index],
      ...record,
      ...(typeof result === "object" ? result : {}),
      modified: false,
      new_record: false,
      ...(result?.id ? { [dataSetPrimaryKey]: result.id } : {}),
    };
    updateTableState(localData, dataSet);

    if (typeof refreshDataSet === "function") {
      await refreshDataSet(dataSet);
    }

    return true;
  };

  const handleDelete = async ({ row }) => {
    const confirmed = window.confirm("Are you sure you want to delete this record?");
    if (!confirmed) {
      return false;
    }

    const record = {
      ...(tableData[row.index] || row.original || {}),
      archived_date: new Date().toISOString(),
      last_edited_by: user,
    };

    const result = await persistRecord(record);
    if (result && result.result === false) {
      setErrorMessage(result.error_message || "Failed to delete record.");
      setShowAlert(true);
      return false;
    }

    const localData = tableData.filter((_, idx) => idx !== row.index);
    updateTableState(localData, dataSet);
    return true;
  };

  const addRecord = () => {
    if (!canAddRecords) {
      return;
    }

    const localData = [...tableData];
    localData.push({
      property_id: propertyid ?? filter?.propertyid ?? null,
      sort_order: tableData.length + 1,
      preamble: "",
      number_of_lifts: 0,
      created_by: user,
      last_edited_by: user,
      new_record: true,
      modified: true,
    });

    updateTableState(localData, dataSet);
  };

  const editableColumns = [
    columnHelper.accessor("lift_operator_id", {
      id: "lift_operator_id",
      header: "ID",
      size: 80,
    }),
    columnHelper.accessor("sort_order", {
      id: "sort_order",
      header: "Sort Order",
      cell: EditableCell,
      size: 120,
      meta: { numericOnly: true },
    }),
    columnHelper.accessor("preamble", {
      id: "preamble",
      header: "Preamble",
      cell: EditableCell,
      size: 320,
      meta: { wrap: true },
    }),
    columnHelper.accessor("entity_id_lift_operator", {
      id: "entity_id_lift_operator",
      header: "Name",
      size: 360,
      sortingFn: sortByLiftOperatorName,
      cell: ({ row }) => {
        const selectedValue = row.original?.entity_id_lift_operator ?? null;
        const selectedOption =
          options.find((option) => String(option.value) === String(selectedValue)) || null;

        return (
          <Select
            isClearable
            value={selectedOption}
            classNamePrefix="react-select"
            onChange={async (selected) => {
              const patch = {
                entity_id_lift_operator: selected?.value || null,
                name: selected?.label || null,
              };

              updateRowPatch(row.index, patch);

              await saveRow(row.index, patch);
            }}
            options={options}
            {...selectMenuProps}
          />
        );
      },
    }),
    columnHelper.accessor("number_of_lifts", {
      id: "number_of_lifts",
      header: "No. Lifts",
      cell: EditableCell,
      size: 130,
      meta: { numericOnly: true },
    }),
    columnHelper.display({
      id: "delete",
      header: "",
      size: 90,
      enableSorting: false,
      cell: (e) => (
        <Button type="button" size="sm" variant="danger" onClick={() => handleDelete({ row: e.row })}>
          Delete
        </Button>
      ),
    }),
  ];

  const summaryColumns = [
    columnHelper.accessor("property_key", {
      id: "property_key",
      header: "Cityscope Reference",
      size: 180,
      cell: (e) => {
        const value = e.getValue();
        return value ? <Link to={`/properties/${value}`}>{value}</Link> : "";
      },
    }),
    columnHelper.accessor("sort_order", {
      id: "sort_order_summary",
      header: "Sort Order",
      size: 120,
    }),
    columnHelper.accessor("preamble", {
      id: "preamble_summary",
      header: "Preamble",
      size: 320,
      meta: { wrap: true },
    }),
    columnHelper.accessor("name", {
      id: "name_summary",
      header: "Name",
      size: 360,
      sortingFn: sortByLiftOperatorName,
    }),
    columnHelper.accessor("number_of_lifts", {
      id: "number_of_lifts_summary",
      header: "No. Lifts",
      size: 130,
    }),
  ];

  return (
    <div className="LiftOperatorList">
      {warningMessage && <Alert variant="warning">{warningMessage}</Alert>}
      <Modal show={showAlert} onHide={() => setShowAlert(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Uh Oh!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger" className="mb-0">
            {errorMessage}
          </Alert>
        </Modal.Body>
      </Modal>

      {canAddRecords ? (
        <Row>
          <Col className="text-right" style={{ marginBottom: "12px" }}>
            <Button variant="secondary" size="sm" onClick={addRecord}>
              Add
            </Button>
          </Col>
        </Row>
      ) : null}

      <Row className="show-Container">
        <Col sm={12} md={12} lg={12}>
          {isAuthenticated ? (
            <GenericTable
              section="lift-operators"
              columns={isSummaryMode ? summaryColumns : editableColumns}
              data={tableData}
              pageSize={resolvedPageSize}
              elementIds={tableElementIds}
              columnResizeMode="onChange"
              meta={{
                updateData: (rowIndex, columnId, value) => {
                  const localData = [...tableData];
                  if (!localData[rowIndex]) {
                    return;
                  }

                  localData[rowIndex] = {
                    ...localData[rowIndex],
                    [columnId]: value,
                    modified: true,
                    last_edited_by: user,
                  };

                  updateTableState(localData, dataSet);
                },
                handleSave: handleInlineSave,
                handleDelete,
              }}
            />
          ) : null}
        </Col>
      </Row>
    </div>
  );
}

export default LiftOperatorList;
